import React from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useThemePalette } from '@/config/theme';
import type { MatchPrediction as MatchPredictionData, TeamRef } from '@/lib/data/provider';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';

/**
 * The pre-match model forecast: a win / draw / win probability bar in the shared
 * team-colour language (home = volt, draw = muted draw, away = teamAway) with the
 * three percentages above it and the most-likely scoreline shown as a hero. The
 * bar is laid out LTR so home always owns the left segment; the surrounding flex
 * rows mirror naturally in RTL. The caller hides this whole block when
 * `prediction` is null.
 */
export function MatchPrediction({
  prediction,
  homeTeam,
  awayTeam,
}: {
  prediction: MatchPredictionData;
  homeTeam: TeamRef | null;
  awayTeam: TeamRef | null;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { locale, arabicNumerals } = useLocale();

  // The stored prediction blob (Bzzoiro) is NOT schema-validated — only `markets`
  // is guaranteed truthy. `match_result`, `score` and `model` are each optional in
  // the feed, so we must read them defensively or a bare upcoming-match forecast
  // throws and takes down the whole match screen.
  const matchResult = prediction.markets?.match_result ?? null;
  const score = prediction.markets?.score ?? null;
  const probHome = Math.max(matchResult?.prob_home ?? 0, 0);
  const probDraw = Math.max(matchResult?.prob_draw ?? 0, 0);
  const probAway = Math.max(matchResult?.prob_away ?? 0, 0);

  // Which outcome the model picks (drives the brighter percentage emphasis).
  const homeLeads = matchResult?.predicted === 'H';
  const drawLeads = matchResult?.predicted === 'D';
  const awayLeads = matchResult?.predicted === 'A';

  // Provider probabilities may be 0–1 fractions or already 0–100; normalise.
  const asPct = (value: number): number => (value <= 1 ? value * 100 : value);
  const pct = (value: number): string =>
    `${localizeDigits(String(Math.round(asPct(value))), arabicNumerals)}%`;

  // The most-likely scoreline reads the same in both directions, so we never
  // mirror its digits (RN-web forces Text direction:ltr on strings anyway).
  const heroScore = score?.most_likely ? localizeDigits(score.most_likely, arabicNumerals) : null;
  const modelVersion = prediction.model?.version ?? null;

  // With neither a 1X2 read nor a scoreline there is nothing to forecast — hide
  // rather than render an empty shell.
  if (!matchResult && !heroScore) return null;

  return (
    <Card className="overflow-hidden">
      <View className="flex-row items-center justify-between px-4 pb-1 pt-3">
        <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
          {t('match.prediction')}
        </AppText>
        {modelVersion ? (
          <AppText variant="micro" weight="medium" className="text-soft" numberOfLines={1}>
            {t('match.predictionModel', { version: modelVersion })}
          </AppText>
        ) : null}
      </View>

      {/* The most-likely scoreline, the headline read of the forecast. */}
      {heroScore ? (
        <View className="items-center gap-0.5 px-4 pb-2 pt-1">
          <AppText variant="title" weight="bold" tabular className="text-ink">
            {heroScore}
          </AppText>
          <AppText variant="micro" weight="medium" className="text-muted">
            {t('match.predictionMostLikely')}
          </AppText>
        </View>
      ) : null}

      {/* The 1X2 read (labels + bar) only renders when the feed carried it; a
          scoreline-only prediction shows just the hero above. */}
      {matchResult ? (
        <>
          {/* Percentage labels above their segments, mirrored with the bar in RTL. */}
          <View className="flex-row items-center justify-between px-4 pb-2 pt-1">
            <AppText
              variant="caption"
              weight="bold"
              tabular
              className="flex-1"
              numberOfLines={1}
              style={{ color: palette.volt, opacity: homeLeads ? 1 : 0.85 }}
            >
              {localizeTeamName(homeTeam, locale, true)} {pct(probHome)}
            </AppText>
            <AppText
              variant="caption"
              weight="bold"
              tabular
              className="text-soft"
              style={{ opacity: drawLeads ? 1 : 0.85 }}
            >
              {pct(probDraw)}
            </AppText>
            <AppText
              variant="caption"
              weight="bold"
              tabular
              className="flex-1 text-right"
              numberOfLines={1}
              style={{ color: palette.teamAway, opacity: awayLeads ? 1 : 0.85 }}
            >
              {pct(probAway)} {localizeTeamName(awayTeam, locale, true)}
            </AppText>
          </View>

          {/* The three-segment bar (LTR so home stays on the left): volt / muted
              draw / teamAway, split by thin canvas-coloured gaps. */}
          <View className="px-4">
            <View
              className="h-2.5 flex-row overflow-hidden rounded-full bg-surface-2"
              style={{ direction: 'ltr' }}
            >
              <View style={{ flex: Math.max(probHome, 0.02), backgroundColor: palette.volt }} />
              <View style={{ width: 2, backgroundColor: palette.deep }} />
              <View
                style={{ flex: Math.max(probDraw, 0.02), backgroundColor: palette.draw, opacity: 0.55 }}
              />
              <View style={{ width: 2, backgroundColor: palette.deep }} />
              <View style={{ flex: Math.max(probAway, 0.02), backgroundColor: palette.teamAway }} />
            </View>
          </View>
        </>
      ) : null}
      {/* Bottom padding closes out the card after the probability bar. */}
      <View className="pb-3.5" />
    </Card>
  );
}
